import { useState, useCallback } from 'react';
import { Image } from 'react-native';
import {
  Flex,
  IconChevronDown,
  IconChevronUp,
  Typography,
} from '@banhala/ui-kit';
import type { CompanyInfoProps } from './types';
import { isNil } from '../../../../utils';
import { moveToWebview } from '../../../../utils/navigator/moveToWebview/moveToWebview';
import { openOutlink } from '../../../../utils/navigator/openOutlink';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - Vite returns string, Metro returns number

const URL_BUSINESS_INFO = 'http://www.ftc.go.kr/www/bizCommList.do?key=232';
const URL_ENTRY_GUIDE = 'https://square.a-bly.com';

const LinkButton = ({ label, onPress }: { label: string; onPress: () => void }) => (
  <Flex
    borderWidth={1}
    borderColor="gray30"
    borderRadius={4}
    padding={{ horizontal: 6, vertical: 4 }}
    onPress={onPress}
  >
    <Typography type="subtitle5" color="content_primary">{label}</Typography>
  </Flex>
);

export const CompanyInfo = ({ entity, body }: CompanyInfoProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const item = body?.item ?? entity?.[0]?.item;

  const toggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  const handleTermsPress = useCallback(() => moveToWebview('TermsOfUse', { titleBar: true }), []);
  const handlePrivacyPress = useCallback(() => moveToWebview('Privacy', { titleBar: true }), []);
  const handleYouthPolicyPress = useCallback(() => moveToWebview('YouthPolicy', { titleBar: true }), []);
  const handleBusinessInfoPress = useCallback(() => openOutlink(URL_BUSINESS_INFO), []);
  const handleEntryGuidePress = useCallback(() => openOutlink(URL_ENTRY_GUIDE), []);

  if (isNil(item)) {
    return null;
  }

  const { company_info_text, legal_text } = item;

  return (
    <Flex backgroundColor="gray10">
      <Flex.Column padding={{ vertical: 20, horizontal: 16 }}>
        {/* Link Buttons */}
        <Flex.Row flexWrap="wrap" gap={8}>
          <LinkButton label="이용약관" onPress={handleTermsPress} />
          <LinkButton label="개인정보 처리방침" onPress={handlePrivacyPress} />
          <LinkButton label="청소년 보호정책" onPress={handleYouthPolicyPress} />
          <LinkButton label="사업자정보확인" onPress={handleBusinessInfoPress} />
          <LinkButton label="입점안내" onPress={handleEntryGuidePress} />
        </Flex.Row>

        {/* Logo + 사업자 정보 Row */}
        <Flex.Row
          alignItems="center"
          justifyContent="space-between"
          padding={{ top: 32 }}
          onPress={toggleExpanded}
        >
          {/* Logo - 87x18dp */}
          <Image source={require('../../../../assets/images/logo_ably.png')} style={{ width: 87, height: 18 }} resizeMode="contain" />

          {/* 사업자 정보 */}
          <Flex.Row alignItems="center">
            <Typography type="body2" color="content_secondary">사업자 정보</Typography>
            <Flex padding={{ left: 4 }}>
              {isExpanded ? (
                <IconChevronUp width={16} height={16} color="content_secondary" />
              ) : (
                <IconChevronDown width={16} height={16} color="content_secondary" />
              )}
            </Flex>
          </Flex.Row>
        </Flex.Row>

        {/* Expandable Company Info */}
        {isExpanded && (
          <Flex padding={{ top: 20 }}>
            <Typography type="body5" color="content_secondary">
              {company_info_text}
            </Typography>
          </Flex>
        )}

        {/* Legal Text */}
        <Flex padding={{ top: 20, bottom: 60 }}>
          <Typography type="body5" color="content_secondary">{legal_text}</Typography>
        </Flex>
      </Flex.Column>
    </Flex>
  );
};
